import MobileBookingSummary from '@/components/booking-summary/MobileBookingSummary';
import RegularBookingSummary from '@/components/booking-summary/RegularBookingSummary';
import Footer from '@/components/Footer';
import { Icons } from '@/components/icons';
import RefundLegModal from '@/components/manage/refund/leg/RefundLegModal';
import RefundNonTripModal from '@/components/manage/refund/non-trip-offer/RefundNonTripModal';
import Tickets from '@/components/manage/tickets/Tickets';
import TravelPasses from '@/components/manage/travel-passes/TravelPasses';
import { Button } from '@/components/primitives/Button';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import { clearManageFlowBooking } from '@/features/manage/manageActions';
import {
  manageFlowBookingSelector,
  manageFlowTravelAccountsMapSelector,
} from '@/features/manage/manageSelectors';
import { useBookingRefundEligibleTrips } from '@/hooks/useBookingRefundEligibleTrips';
import useBookingSummaryData from '@/hooks/useBookingSummaryData';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSeparateNonTripOffersBasedOnType } from '@/hooks/useSeparateNonTripOffersBasedOnType';
import useUsedVouchers from '@/hooks/useUsedVouchers';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Navigate, useNavigate } from 'react-router-dom';

interface BookingDetails {}

export const BookingDetails: FC<BookingDetails> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const booking = useSelector(manageFlowBookingSelector);
  const usedVouchers = useUsedVouchers({ bookingId: booking?.id });
  const travelAccountsMap = useSelector(manageFlowTravelAccountsMapSelector);
  const summaryData = useBookingSummaryData({
    booking,
    passengers: booking?.passengers,
    vouchers: usedVouchers.map(({ voucherCode, ...rest }) => ({
      ...rest,
      voucher_code: voucherCode,
    })),
    displayPrice: booking?.confirmedPrice,
    travelAccountsMap,
    allowedStatuses: ['FULFILLED', 'REFUNDED', 'RELEASED', 'EXCHANGED'],
  });
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const { refundEligibleOutboundTrips, refundEligibleInboundTrips } =
    useBookingRefundEligibleTrips(booking);
  const { travelPass, multiRide } = useSeparateNonTripOffersBasedOnType(
    booking?.nonTripOffers,
    travelAccountsMap
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { t } = useTranslation();
  const areEligibleTripsRefundable = Boolean(
    refundEligibleOutboundTrips.length || refundEligibleInboundTrips.length
  );
  const areEligibleNonTripOffersRefundable = Boolean(
    [...travelPass, ...multiRide].filter(
      ({ status, refundable }) => status !== 'REFUNDED' && refundable === 'YES'
    ).length
  );
  const isRefundButtonVisible =
    areEligibleTripsRefundable || areEligibleNonTripOffersRefundable;

  usePageTitle('manageBooking');

  const handleGoBack = useCallback(() => {
    dispatch(clearManageFlowBooking());
    navigate(-1);
  }, [dispatch, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearManageFlowBooking());
    };
  }, [dispatch]);

  if (!booking) {
    return <Navigate to="/manage-booking" replace />;
  }

  return (
    <div
      className={cn('pb-16 laptop:pb-4', {
        'pb-36 laptop:pb-20': isRefundButtonVisible,
      })}
    >
      <div className="mx-auto flex max-w-laptop items-center gap-2 p-4">
        <Button
          data-testid="go-back-button"
          variant="icon"
          aria-label={t('text.goBack')}
          onClick={handleGoBack}
        >
          <Icons.arrowLeft />
        </Button>

        <div className="flex items-baseline gap-1">
          <Typography variant={isLaptopOrBigger ? 'heading1-light' : 'body1'}>
            <>
              <TransText i18nKey="booking" />
              &#58;
            </>
          </Typography>
          <Typography variant={isLaptopOrBigger ? 'heading1' : 'subtitle'}>
            {booking?.code}
          </Typography>
        </div>
      </div>
      <Divider variant="medium" />
      <div className="mx-auto mt-8 flex max-w-laptop flex-col px-4 laptop:flex-row laptop:gap-10">
        {booking?.bookedTrips?.length ? (
          <Tickets passengersMap={summaryData.passengersMap} />
        ) : (
          <TravelPasses />
        )}
        {isLaptopOrBigger && (
          <div className="flex flex-col gap-7 pb-2">
            <Typography variant="heading2" asChild>
              <h1>
                <TransText i18nKey="bookingSummary" />
              </h1>
            </Typography>
            <RegularBookingSummary {...summaryData} />
          </div>
        )}
      </div>
      <Footer
        content={!isLaptopOrBigger && <MobileBookingSummary {...summaryData} />}
        actionButtons={
          isRefundButtonVisible && (
            <Button
              size={isLaptopOrBigger ? 'medium' : 'large'}
              fullWidth
              data-testid="refund-button"
              variant="secondary"
              className="rounded-lg laptop:w-auto"
              onClick={() => setIsRefundModalOpen(true)}
            >
              <Icons.ticketReturn />
              <TransText i18nKey="refund" />
            </Button>
          )
        }
      />
      <RefundLegModal
        isOpen={areEligibleTripsRefundable && isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
      />
      <RefundNonTripModal
        isOpen={areEligibleNonTripOffersRefundable && isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
      />
    </div>
  );
};
